import { useState, useCallback } from 'react';
import { AlertTriangle, CheckCircle, RefreshCw, Search, Zap } from 'lucide-react';
import { createApiClient, paymentService } from '../services/api';
import type { EarningsReconciliationReport, PaymentReprocessResult } from '../services/api';
import { useAppAuth } from '../hooks/useAppAuth';

function fmt(n: number) { return `£${n.toFixed(2)}`; }
function delta(d: number) { return d >= 0 ? `+${fmt(d)}` : `-${fmt(Math.abs(d))}`; }

// ── Stat card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', minWidth: 120 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ── Drift row ──────────────────────────────────────────────────────────────────
function DriftRow({ drift }: { drift: { carrierId: string; storedEarnings: number; computedEarnings: number; delta: number } }) {
  const isShort = drift.delta < 0;
  return (
    <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
      <td style={s.td}><code style={{ fontSize: 11, color: '#6b7280' }}>{drift.carrierId}</code></td>
      <td style={{ ...s.td, textAlign: 'right' }}>{fmt(drift.storedEarnings)}</td>
      <td style={{ ...s.td, textAlign: 'right' }}>{fmt(drift.computedEarnings)}</td>
      <td style={{ ...s.td, textAlign: 'right' }}>
        <span style={{ color: isShort ? '#dc2626' : '#16a34a', fontWeight: 700 }}>{delta(drift.delta)}</span>
      </td>
    </tr>
  );
}

// ── Reprocess result banner ────────────────────────────────────────────────────
function ReprocessBanner({ result, onDismiss }: { result: PaymentReprocessResult; onDismiss: () => void }) {
  const ok = result.carrierId !== '00000000-0000-0000-0000-000000000000';
  return (
    <div style={{
      background: ok ? '#f0fdf4' : '#fef2f2',
      border: `1px solid ${ok ? '#bbf7d0' : '#fecaca'}`,
      borderRadius: 10, padding: 16, marginTop: 16,
      display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      {ok
        ? <CheckCircle size={18} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
        : <AlertTriangle size={18} color="#dc2626" style={{ flexShrink: 0, marginTop: 2 }} />}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: ok ? '#15803d' : '#b91c1c', marginBottom: 6 }}>
          {ok ? 'Reprocess complete' : 'Reprocess failed'}
        </div>
        {ok && (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
            <Pill label="Earnings patched" value={result.earningsPatched ? 'Yes' : 'No'} color={result.earningsPatched ? '#15803d' : '#6b7280'} />
            {result.earningsPatched && (
              <Pill label="Change" value={`${fmt(result.oldEarnings)} → ${fmt(result.newEarnings)}`} color="#1d4ed8" />
            )}
            <Pill label="Notification sent" value={result.notificationSent ? 'Yes' : 'No'} color={result.notificationSent ? '#15803d' : '#6b7280'} />
          </div>
        )}
        <div style={{ fontSize: 12, color: '#374151' }}>{result.message}</div>
      </div>
      <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 16, padding: 0 }}>✕</button>
    </div>
  );
}

function Pill({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ fontSize: 11 }}>
      <span style={{ color: '#9ca3af' }}>{label}: </span>
      <span style={{ color, fontWeight: 700 }}>{value}</span>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function AdminPaymentReconciliationPage() {
  const { getToken } = useAppAuth();
  const client = createApiClient(getToken);
  const svc = paymentService(client);

  // Full sweep
  const [sweeping, setSweeping]         = useState(false);
  const [report, setReport]             = useState<EarningsReconciliationReport | null>(null);
  const [sweepError, setSweepError]     = useState<string | null>(null);

  // Individual reprocess
  const [paymentIdInput, setPaymentIdInput] = useState('');
  const [reprocessing, setReprocessing]     = useState(false);
  const [reprocessResult, setReprocessResult] = useState<PaymentReprocessResult | null>(null);
  const [reprocessError, setReprocessError]   = useState<string | null>(null);

  const runSweep = useCallback(async () => {
    setSweeping(true);
    setSweepError(null);
    try {
      const r = await svc.reconcileEarnings();
      setReport(r);
    } catch {
      setSweepError('Reconciliation sweep failed. Check server logs.');
    } finally {
      setSweeping(false);
    }
  }, [svc]);

  const runReprocess = useCallback(async () => {
    const id = paymentIdInput.trim();
    if (!id) return;
    setReprocessing(true);
    setReprocessError(null);
    setReprocessResult(null);
    try {
      const r = await svc.reprocessPayment(id);
      setReprocessResult(r);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Reprocess request failed.';
      setReprocessError(msg);
    } finally {
      setReprocessing(false);
    }
  }, [paymentIdInput, svc]);

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Payment Reconciliation</h1>
          <p style={s.subtitle}>
            Detect and fix earnings drift between <code>CarrierProfile.TotalEarnings</code> and actual
            Released payments. Re-send missed notifications for individual payments.
          </p>
        </div>
      </div>

      {/* ── Full sweep ─────────────────────────────────────────────────────── */}
      <div style={s.section}>
        <div style={s.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <RefreshCw size={18} color="#7c3aed" />
            <h2 style={s.sectionTitle}>Platform-wide Earnings Sweep</h2>
          </div>
          <p style={s.sectionDesc}>
            Scans every carrier with Released payments and patches any <code>TotalEarnings</code> drift.
            The background service runs this automatically every 30 minutes — use this button for an immediate sweep.
          </p>
        </div>

        <button
          onClick={runSweep}
          disabled={sweeping}
          style={{ ...s.btn, ...s.btnPrimary, opacity: sweeping ? 0.6 : 1, alignSelf: 'flex-start' }}
        >
          {sweeping
            ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Running sweep…</>
            : <><Zap size={14} /> Run Full Reconciliation Sweep</>}
        </button>

        {sweepError && (
          <div style={{ ...s.errorBanner, marginTop: 12 }}>
            <AlertTriangle size={14} color="#dc2626" />{sweepError}
          </div>
        )}

        {report && (
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <StatCard label="Carriers Checked"  value={report.carriersChecked}  color="#374151" />
              <StatCard label="Carriers Patched"  value={report.carriersPatched}  color={report.carriersPatched > 0 ? '#dc2626' : '#16a34a'} />
              <StatCard label="Last Run"          value={new Date(report.ranAt).toLocaleTimeString('en-GB')} color="#6b7280" />
            </div>

            {report.carriersPatched === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <CheckCircle size={16} color="#16a34a" />
                <span style={{ fontSize: 13, color: '#15803d', fontWeight: 600 }}>All carrier earnings are consistent — no drift detected.</span>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#dc2626', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={14} color="#dc2626" />
                  {report.carriersPatched} carrier{report.carriersPatched !== 1 ? 's' : ''} patched — all corrected automatically.
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Carrier ID</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Was Stored</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Correct Value</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Delta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.drifts.map(d => <DriftRow key={d.carrierId} drift={d} />)}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Individual reprocess ───────────────────────────────────────────── */}
      <div style={s.section}>
        <div style={s.sectionHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={18} color="#047857" />
            <h2 style={s.sectionTitle}>Reprocess Individual Payment</h2>
          </div>
          <p style={s.sectionDesc}>
            For a specific Released payment: patches carrier earnings if drifted, then re-sends the
            payment-released notification to the carrier. Use this when a carrier reports missing funds
            or a missed notification after a Stripe webhook failure or manual DB edit.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Payment ID (UUID) — e.g. 3fa85f64-5717-4562-b3fc-2c963f66afa6"
            value={paymentIdInput}
            onChange={e => setPaymentIdInput(e.target.value)}
            style={s.input}
            onKeyDown={e => e.key === 'Enter' && runReprocess()}
          />
          <button
            onClick={runReprocess}
            disabled={reprocessing || !paymentIdInput.trim()}
            style={{ ...s.btn, ...s.btnSuccess, opacity: (reprocessing || !paymentIdInput.trim()) ? 0.5 : 1, whiteSpace: 'nowrap' }}
          >
            {reprocessing ? <><RefreshCw size={14} /> Processing…</> : 'Reprocess Payment'}
          </button>
        </div>

        {reprocessError && (
          <div style={{ ...s.errorBanner, marginTop: 12 }}>
            <AlertTriangle size={14} color="#dc2626" />{reprocessError}
          </div>
        )}

        {reprocessResult && (
          <ReprocessBanner
            result={reprocessResult}
            onDismiss={() => setReprocessResult(null)}
          />
        )}

        {/* Usage hint */}
        <div style={{ marginTop: 20, padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>When to use this</div>
          <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: '#78350f', lineHeight: 1.7 }}>
            <li>A carrier reports their Payments Board shows £0.00 after a successful customer payment</li>
            <li>A carrier did not receive a payment notification (e.g. after a manual DB fix)</li>
            <li>A Stripe webhook fired but the application had a transient error during side-effect processing</li>
            <li>Only works on payments already in <strong>Released</strong> status — use the Verification Queue for Pending/Processing payments</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:          { width: '100%', padding: '28px 20px' },
  header:        { marginBottom: 24 },
  h1:            { fontSize: 22, fontWeight: 800, color: '#111827', margin: '0 0 6px' },
  subtitle:      { fontSize: 13, color: '#6b7280', margin: 0, lineHeight: 1.6 },
  section:       { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, marginBottom: 20 },
  sectionHeader: { marginBottom: 16 },
  sectionTitle:  { fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 },
  sectionDesc:   { fontSize: 12, color: '#6b7280', margin: '6px 0 0', lineHeight: 1.6 },
  btn:           { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  btnPrimary:    { background: '#7c3aed', color: '#fff' },
  btnSuccess:    { background: '#047857', color: '#fff' },
  input:         { flex: 1, minWidth: 320, padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, color: '#111827', outline: 'none', fontFamily: 'monospace' },
  table:         { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th:            { padding: '8px 12px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: 0.5, borderBottom: '2px solid #f3f4f6' },
  td:            { padding: '10px 12px', color: '#374151', verticalAlign: 'middle' as const },
  errorBanner:   { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#b91c1c', fontWeight: 600 },
};
